import { useState, useMemo } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Users, Store, ArrowRightLeft, Scissors, History,
  ChevronDown, ChevronUp, Calendar,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PersonnelSection({ staff, zones, allZones, allBarbers, onChanged, getAuthHeaders }) {
  const [moveDialog, setMoveDialog] = useState({ open: false, barber: null });
  const [targetBranch, setTargetBranch] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [expandedBarberId, setExpandedBarberId] = useState(null);
  const [movementsCache, setMovementsCache] = useState({});
  const [loadingMovements, setLoadingMovements] = useState(null);
  const [showForeign, setShowForeign] = useState(false);

  // Flatten all managed branches from user's zones (source: barberos que puedo mover)
  const managedBranches = useMemo(() => {
    const acc = [];
    const seen = new Set();
    (zones || []).forEach((z) => {
      (z.branches || []).forEach((b) => {
        if (b?.id && !seen.has(b.id)) {
          seen.add(b.id);
          acc.push({ id: b.id, name: b.name, zone_name: z.name });
        }
      });
    });
    return acc;
  }, [zones]);

  // Destination: TODAS las sucursales (permite mover a zonas ajenas).
  const allBranches = useMemo(() => {
    const acc = [];
    const seen = new Set();
    (allZones || []).forEach((z) => {
      (z.branches || []).forEach((b) => {
        if (b?.id && !seen.has(b.id)) {
          seen.add(b.id);
          acc.push({ id: b.id, name: b.name, zone_name: z.name, is_mine: false });
        }
      });
    });
    // Marca las sucursales de las zonas propias
    const mine = new Set(managedBranches.map((b) => b.id));
    acc.forEach((b) => {
      if (mine.has(b.id)) b.is_mine = true;
    });
    // Ordena: propias primero, luego por zona
    acc.sort((a, b) => {
      if (a.is_mine !== b.is_mine) return a.is_mine ? -1 : 1;
      return (a.zone_name || '').localeCompare(b.zone_name || '') || a.name.localeCompare(b.name);
    });
    return acc;
  }, [allZones, managedBranches]);

  const employees = staff?.employees || [];

  // Group employees by branch (mis barberos)
  const grouped = useMemo(() => {
    const map = new Map();
    managedBranches.forEach((b) => map.set(b.id, { branch: b, employees: [] }));
    employees.forEach((e) => {
      const bucket = map.get(e.branch_id);
      if (bucket) bucket.employees.push(e);
    });
    return Array.from(map.values());
  }, [employees, managedBranches]);

  // Barberos que NO están en mis sucursales → agrupados por sucursal ajena
  const foreignGrouped = useMemo(() => {
    const mineIds = new Set(managedBranches.map((b) => b.id));
    const branchMap = new Map();
    allBranches.forEach((b) => {
      if (!mineIds.has(b.id)) branchMap.set(b.id, { branch: b, employees: [] });
    });
    (allBarbers || []).forEach((b) => {
      if (!mineIds.has(b.branch_id)) {
        const bucket = branchMap.get(b.branch_id);
        if (bucket) bucket.employees.push(b);
      }
    });
    return Array.from(branchMap.values()).filter((g) => g.employees.length > 0);
  }, [allBarbers, allBranches, managedBranches]);

  const foreignCount = foreignGrouped.reduce((n, g) => n + g.employees.length, 0);

  const openMoveDialog = (barber) => {
    setMoveDialog({ open: true, barber });
    setTargetBranch('');
    setReason('');
  };

  const submitMove = async () => {
    if (!targetBranch) {
      toast.error('Selecciona la sucursal destino');
      return;
    }
    if (targetBranch === moveDialog.barber?.branch_id) {
      toast.error('El barbero ya está en esa sucursal');
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/barbers/${moveDialog.barber.id}/branch`,
        { branch_id: targetBranch, reason: reason || null },
        getAuthHeaders()
      );
      const targetName = allBranches.find((b) => b.id === targetBranch)?.name || 'nueva sucursal';
      toast.success(`${moveDialog.barber.name} → ${targetName}`);
      setMoveDialog({ open: false, barber: null });
      // Invalidate cache for this barber
      setMovementsCache((c) => {
        const { [moveDialog.barber.id]: _, ...rest } = c;
        return rest;
      });
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo mover al barbero');
    } finally {
      setSaving(false);
    }
  };

  const toggleHistory = async (barberId) => {
    if (expandedBarberId === barberId) {
      setExpandedBarberId(null);
      return;
    }
    setExpandedBarberId(barberId);
    if (!movementsCache[barberId]) {
      setLoadingMovements(barberId);
      try {
        const res = await axios.get(`${API}/barbers/${barberId}/movements`, getAuthHeaders());
        setMovementsCache((c) => ({ ...c, [barberId]: res.data.movements || [] }));
      } catch (_e) {
        setMovementsCache((c) => ({ ...c, [barberId]: [] }));
      } finally {
        setLoadingMovements(null);
      }
    }
  };

  if (managedBranches.length === 0) {
    return (
      <Card className="bg-zinc-900/50 border-zinc-800 border-dashed p-8 text-center">
        <Users className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
        <p className="text-zinc-500">Aún no tienes sucursales asignadas a tu zona.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="personnel-section">
      <Card className="bg-zinc-900/50 border-zinc-800 p-4">
        <div className="flex items-center gap-2 text-sm text-zinc-400">
          <Users className="w-4 h-4 text-gold" />
          <span>
            Gestiona la asignación de barberos entre tus {managedBranches.length} sucursal
            {managedBranches.length !== 1 ? 'es' : ''} o mueve a cualquier barbero del negocio a cualquier sucursal.
          </span>
        </div>
      </Card>

      {grouped.map(({ branch, employees: branchEmployees }) => (
        <Card key={branch.id} className="bg-zinc-900/50 border-zinc-800" data-testid={`personnel-branch-${branch.id}`}>
          <div className="flex items-center justify-between p-4 border-b border-zinc-800">
            <div className="flex items-center gap-3">
              <Store className="w-5 h-5 text-gold" />
              <div>
                <p className="text-white font-medium">{branch.name}</p>
                <p className="text-xs text-zinc-500">{branch.zone_name}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-heading text-xl text-white">{branchEmployees.length}</p>
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider">barbero{branchEmployees.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          {branchEmployees.length === 0 ? (
            <p className="p-6 text-center text-zinc-500 text-sm">Sin personal asignado en esta sucursal.</p>
          ) : (
            <div className="divide-y divide-zinc-800">
              {branchEmployees.map((e) => (
                <div key={e.id} data-testid={`personnel-row-${e.id}`}>
                  <div className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center">
                        <Scissors className="w-4 h-4 text-zinc-400" />
                      </div>
                      <div>
                        <p className="text-white text-sm font-medium">{e.name}</p>
                        <p className="text-[11px] text-zinc-500">
                          {e.role_type === 'stylist' ? 'Estilista' : 'Barbero'} · @{e.username || '—'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-8"
                        onClick={() => toggleHistory(e.id)}
                        data-testid={`personnel-history-btn-${e.id}`}
                      >
                        <History className="w-3.5 h-3.5 mr-1" />
                        Historial
                        {expandedBarberId === e.id ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
                      </Button>
                      <Button
                        size="sm"
                        className="bg-gold text-black hover:bg-gold/90 h-8"
                        onClick={() => openMoveDialog(e)}
                        data-testid={`personnel-move-btn-${e.id}`}
                      >
                        <ArrowRightLeft className="w-3.5 h-3.5 mr-1" />
                        Mover
                      </Button>
                    </div>
                  </div>
                  {expandedBarberId === e.id && (
                    <div className="px-4 pb-4 bg-zinc-950/40" data-testid={`personnel-history-${e.id}`}>
                      {loadingMovements === e.id ? (
                        <p className="text-xs text-zinc-500 py-3">Cargando historial...</p>
                      ) : (movementsCache[e.id] || []).length === 0 ? (
                        <p className="text-xs text-zinc-500 py-3">Sin movimientos registrados aún.</p>
                      ) : (
                        <ul className="space-y-1.5 py-3">
                          {movementsCache[e.id].map((m) => (
                            <li key={m.id} className="text-xs flex items-start gap-2 text-zinc-400">
                              <Calendar className="w-3 h-3 mt-0.5 shrink-0" />
                              <span className="text-zinc-500">{new Date(m.moved_at).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                              <span className="text-zinc-700">·</span>
                              <span>
                                <span className="text-red-400">{m.from_branch_name || '—'}</span>
                                <ArrowRightLeft className="inline w-3 h-3 mx-1 text-zinc-600" />
                                <span className="text-emerald-400">{m.to_branch_name}</span>
                              </span>
                              <span className="text-zinc-700">·</span>
                              <span className="text-zinc-500">por {m.moved_by_name}</span>
                              {m.reason && <span className="text-zinc-500 italic">— &quot;{m.reason}&quot;</span>}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      ))}

      {foreignCount > 0 && (
        <Card className="bg-zinc-900/30 border-zinc-800 border-dashed" data-testid="personnel-foreign-section">
          <button
            type="button"
            onClick={() => setShowForeign((v) => !v)}
            className="w-full flex items-center justify-between p-4 hover:bg-zinc-900/50 transition-colors"
            data-testid="personnel-foreign-toggle"
          >
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-zinc-500" />
              <div className="text-left">
                <p className="text-white text-sm font-medium">Otros barberos ({foreignCount})</p>
                <p className="text-xs text-zinc-500">Personal fuera de tus zonas · también puedes moverlos</p>
              </div>
            </div>
            {showForeign ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
          </button>
          {showForeign && (
            <div className="border-t border-zinc-800 divide-y divide-zinc-800">
              {foreignGrouped.map(({ branch, employees: fEmployees }) => (
                <div key={branch.id} data-testid={`personnel-foreign-branch-${branch.id}`}>
                  <div className="flex items-center justify-between px-4 py-3 bg-zinc-900/40">
                    <div className="flex items-center gap-2">
                      <Store className="w-4 h-4 text-zinc-500" />
                      <div>
                        <p className="text-sm text-white">{branch.name}</p>
                        <p className="text-[10px] text-zinc-500">{branch.zone_name}</p>
                      </div>
                    </div>
                    <span className="text-xs text-zinc-500">{fEmployees.length} barbero{fEmployees.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="divide-y divide-zinc-800/60">
                    {fEmployees.map((e) => (
                      <div key={e.id} className="flex items-center justify-between px-4 py-3" data-testid={`personnel-foreign-row-${e.id}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-zinc-800/70 flex items-center justify-center">
                            <Scissors className="w-3.5 h-3.5 text-zinc-500" />
                          </div>
                          <div>
                            <p className="text-white text-sm">{e.name}</p>
                            <p className="text-[11px] text-zinc-500">
                              {e.role_type === 'stylist' ? 'Estilista' : 'Barbero'}
                              {e.username ? ` · @${e.username}` : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-8"
                            onClick={() => toggleHistory(e.id)}
                            data-testid={`personnel-foreign-history-btn-${e.id}`}
                          >
                            <History className="w-3.5 h-3.5 mr-1" />
                            Historial
                          </Button>
                          <Button
                            size="sm"
                            className="bg-gold text-black hover:bg-gold/90 h-8"
                            onClick={() => openMoveDialog(e)}
                            data-testid={`personnel-foreign-move-btn-${e.id}`}
                          >
                            <ArrowRightLeft className="w-3.5 h-3.5 mr-1" />
                            Mover
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      <Dialog open={moveDialog.open} onOpenChange={(o) => !o && setMoveDialog({ open: false, barber: null })}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md" data-testid="personnel-move-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-gold" />
              Mover {moveDialog.barber?.name}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Puedes reasignar el barbero a cualquier sucursal, incluyendo zonas que no gestionas. Su historial de comisiones se preserva.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">Sucursal actual</Label>
              <p className="text-white mt-1">
                {allBranches.find((b) => b.id === moveDialog.barber?.branch_id)?.name || '—'}
              </p>
            </div>
            <div>
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">Sucursal destino</Label>
              <Select value={targetBranch} onValueChange={setTargetBranch}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="personnel-target-branch-select">
                  <SelectValue placeholder="Selecciona una sucursal..." />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 max-h-72">
                  {allBranches
                    .filter((b) => b.id !== moveDialog.barber?.branch_id)
                    .map((b) => (
                      <SelectItem key={b.id} value={b.id} className="text-white">
                        <span className={b.is_mine ? 'text-gold' : ''}>{b.name}</span>
                        <span className="text-zinc-500 text-xs ml-2">
                          ({b.zone_name}{b.is_mine ? ' · tu zona' : ''})
                        </span>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">Motivo (opcional)</Label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ej: cobertura temporal por licencia"
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                data-testid="personnel-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              onClick={() => setMoveDialog({ open: false, barber: null })}
              data-testid="personnel-cancel-btn"
            >
              Cancelar
            </Button>
            <Button
              className="bg-gold text-black hover:bg-gold/90"
              onClick={submitMove}
              disabled={saving || !targetBranch}
              data-testid="personnel-confirm-move-btn"
            >
              {saving ? 'Moviendo...' : 'Confirmar movimiento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
